import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { ActivatedRoute, Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { BehaviorSubject, debounceTime, distinctUntilChanged, EMPTY, from, forkJoin, MonoTypeOperatorFunction, Observable, of, Subject } from 'rxjs';
import { catchError, concatMap, first, map, tap, toArray } from 'rxjs/operators';
import { Counter, indicate, indicateWithCounter, retryTransient } from 'src/app/operators';
import { DownloadService } from 'src/app/core/services/download.service';
import { GraphService } from 'src/app/core/services/graph.service';
import * as MicrosoftGraph from '@microsoft/microsoft-graph-types';
import { ReleaseFinalChecksExportService } from './services/release-final-checks-export.service';
import {
  ApiException,
  BulkInactiveComponentRequestDto,
  ComponentActivationRequestDto,
  ComponentRemovalRequestDto,
  ComponentReleaseAsIsRequestDto,
  ComponentReplacementRequestDto,
  EsraPipingAPIClient,
  ExecuteReleaseRequestDto,
  FileResponse,
  FinalCheckSectionSummary,
  InactiveComponentGroupDto,
  InactiveComponentPipeClassRefDto,
  InactiveComponentProceedWithoutUpdatesRequestDto,
  InactiveComponentResolutionAction,
  InactiveComponentsValidationDto,
  PipingClassStatusRowDto,
  PipingClassStatusValidationDto,
  ReleaseAttributesDto,
  ReleaseDetailViewModel,
  ReleaseFinalChecksSummaryDto,
  ReleaseRoleViewModel,
  ReleaseSelectedObject,
  ReleaseStatus,
  RevisionStatus,
  UserType,
  ValidationSeverity,
  ValveActivationRequestDto,
  ValveDescriptionBulkChoiceRequestDto,
  ValveDescriptionChoiceOption,
  ValveDescriptionChoiceRequestDto,
  ValveDescriptionRowDto,
  ValveDescriptionValidationDto,
  ValveProceedWithoutUpdatesRequestDto,
  ValveStatusRowDto,
  ValveStatusValidationDto,
  ValveVersionUpdateRequestDto,
  ValveVersionUpdateResultDto,
  ValveVersionUpdateSkipDto,
} from 'src/app/shared/models/autogenerated-piping';
import { ConfirmDialogComponent } from 'src/app/shared/confirm-dialog/confirm-dialog.component';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { UserService } from 'src/app/core/services/user.service';
import { Constants } from 'src/app/shared/constants/constants';
import { RevisionStatusHelper } from 'src/app/shared/models/revision-status.model';
import { getApiErrorMessage } from 'src/app/shared/utils';
import { ComponentCatalogModalComponent, ComponentCatalogSelectionData, ComponentOption } from 'src/app/search/piping-summary/component-catalog-modal/component-catalog-modal.component';

/** Maps a RevisionStatus numeric enum value to its display label. */
function getRevisionStatusLabel(status: RevisionStatus | undefined): string {
  switch (status) {
    case RevisionStatus.DRAFT: return 'DRAFT';
    case RevisionStatus.REVIEW: return 'REVIEW';
    case RevisionStatus.APPROVAL: return 'APPROVAL';
    case RevisionStatus.PUBLISHED: return 'PUBLISHED';
    case RevisionStatus.RETIRED: return 'RETIRED';
    case RevisionStatus.REJECTED: return 'REJECTED';
    default: return '—';
  }

type ValveDescriptionDisplayRow = ValveDescriptionRowDto & {
  pipeClassDisplayName?: string;
};
}

/** Maps ReleaseSelectedObject enum to a display label. */
function getPipeClassActionLabel(action: ReleaseSelectedObject | undefined): string {
  switch (action) {
    case ReleaseSelectedObject._0: return 'Release As-Is';
    case ReleaseSelectedObject._1: return 'Revise';
    case ReleaseSelectedObject._2: return 'Retire';
    default: return '—';
  }
}

/** Maps an InactiveComponentResolutionAction enum value to its display label (AC5). */
function getInactiveResolutionActionLabel(action: InactiveComponentResolutionAction | undefined): string {
  switch (action) {
    case InactiveComponentResolutionAction._0: return 'Pending';
    case InactiveComponentResolutionAction._1: return 'Replaced';
    case InactiveComponentResolutionAction._2: return 'Activated';
    case InactiveComponentResolutionAction._3: return 'Released As-Is';
    case InactiveComponentResolutionAction._4: return 'Removed From Revision';
    default: return 'Pending';
  }
}

/** Returns the CSS status-badge class for an InactiveComponentResolutionAction value. */
function getInactiveResolutionActionCssClass(action: InactiveComponentResolutionAction | undefined): string {
  switch (action) {
    case InactiveComponentResolutionAction._1:
    case InactiveComponentResolutionAction._2:
    case InactiveComponentResolutionAction._3:
    case InactiveComponentResolutionAction._4:
      return 'badge-ok';
    default:
      return 'badge-error';
  }
}

/** Returns the CSS severity class for a ValidationSeverity value. */
function getSeverityCssClass(severity: ValidationSeverity | undefined): string {
  switch (severity) {
    case ValidationSeverity._0: return 'severity-info';
    case ValidationSeverity._1: return 'severity-warning';
    case ValidationSeverity._2: return 'severity-user-overridable';
    case ValidationSeverity._3: return 'severity-blocking';
    case ValidationSeverity._4: return 'severity-draft';
    default: return '';
  }
}

/** Returns the severity label for a ValidationSeverity value. */
function getSeverityLabel(severity: ValidationSeverity | undefined): string {
  switch (severity) {
    case ValidationSeverity._0: return 'Info';
    case ValidationSeverity._1: return 'Warning';
    case ValidationSeverity._2: return 'Override';
    case ValidationSeverity._3: return 'Blocking';
    case ValidationSeverity._4: return 'Draft';
    default: return '';
  }
}

@Component({
  selector: 'app-release-final-checks',
  templateUrl: './release-final-checks.component.html',
  styleUrls: ['./release-final-checks.component.scss'],
  standalone: false,
})
export class ReleaseFinalChecksComponent implements OnInit {
  private static readonly VALVE_STATUS_TAB_INDEX: number = 1;
  private static readonly VALVE_DESCRIPTIONS_TAB_INDEX: number = 2;

  // ── Icons ─────────────────────────────────────────────────────────────
  readonly faChevronLeft = faChevronLeft;

  // ── Static helpers exposed to the template ────────────────────────────
  readonly getRevisionStatusLabel: (s: RevisionStatus | undefined) => string = getRevisionStatusLabel;
  readonly getPipeClassActionLabel: (a: ReleaseSelectedObject | undefined) => string = getPipeClassActionLabel;
  readonly getSeverityCssClass: (s: ValidationSeverity | undefined) => string = getSeverityCssClass;
  readonly getSeverityLabel: (s: ValidationSeverity | undefined) => string = getSeverityLabel;
  readonly getInactiveResolutionActionLabel: (a: InactiveComponentResolutionAction | undefined) => string = getInactiveResolutionActionLabel;
  readonly getInactiveResolutionActionCssClass: (a: InactiveComponentResolutionAction | undefined) => string = getInactiveResolutionActionCssClass;

  /** Enum references for template comparisons. */
  readonly ValidationSeverity: typeof ValidationSeverity = ValidationSeverity;
  readonly ReleaseSelectedObject: typeof ReleaseSelectedObject = ReleaseSelectedObject;
  readonly ValveDescriptionChoiceOption: typeof ValveDescriptionChoiceOption = ValveDescriptionChoiceOption;
  readonly InactiveComponentResolutionAction: typeof InactiveComponentResolutionAction = InactiveComponentResolutionAction;
  readonly RevisionStatus: typeof RevisionStatus = RevisionStatus;

  // ── Route params ──────────────────────────────────────────────────────
  releaseId: string = '';

  /** Passed to `app-export-excel-button`; the download filename comes from the response's content-disposition header. */
  exportInactiveComponentsFn: () => Observable<FileResponse> = () => this.apiClient.exportInactiveComponents(this.releaseId);

  /**
   * Passed to `app-export-excel-button` on the Valve Status tab. The workbook is generated
   * server-side (a real .xlsx, unlike the previous client-side CSV that Excel refused to open)
   * and the download filename comes from the response's content-disposition header.
   */
  exportValveStatusFn: () => Observable<FileResponse> = () => this.apiClient.exportValveStatus(this.releaseId);

  // ── Loading indicators ────────────────────────────────────────────────
  /** True while the overall summary (badge data) is being fetched. */
  loadingSummary$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  /** True while the current active tab data is loading. */
  loadingTab$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  /** True while a background refresh is in flight; shown as a small spinner next to the title, never hides content. */
  refreshing$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private readonly refreshCounter: Counter = new Counter();
  /** True while the Release (publish) API call is in-flight. */
  releasing$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  /** True while a valve description choice PUT is in-flight; disables all radio buttons during the request. */
  savingDescriptionChoice$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  /** True while a Replace/Activate/Release As-Is call is in-flight for an inactive component group. */
  resolvingInactiveComponent$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  /** True while a selected inactive-component bulk action is in-flight. */
  applyingInactiveComponentBulkAction$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  /** True while the inactive-components proceed-without-updates acknowledgement is being persisted. */
  savingInactiveComponentsProceedWithoutUpdates$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  /** True while the bulk "update to newer version" call is in-flight. */
  updatingValveVersions$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  /** True while an enable-valve call is in-flight. */
  enablingValve$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  /** True while the release-wide "proceed without updates" toggle is being persisted. */
  savingProceedWithoutUpdates$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  // ── Summary (drives tab badges) ───────────────────────────────────────
  summary: ReleaseFinalChecksSummaryDto | null = null;

  /**
   * True when the current user is the release author or one of its owners.
   * Non-author/non-owner users must not be able to publish the release, so the
   * Release button is hidden when this is false.
   */
  canManageRelease: boolean = false;

  /**
   * True when the user holds one of the roles required to disable a valve in the component
   * catalog, and may therefore also re-enable one from this page. Mirrors the backend guard on
   * `POST final-checks/valve-status/enable` (Piping.Admin / Piping.Dictionary.Write).
   */
  canEnableValves: boolean = false;

  /** Lifecycle status of the parent release; publishing is blocked while Draft or once Published. */
  releaseStatus: ReleaseStatus | undefined;

  // ── Tab data ──────────────────────────────────────────────────────────
  valveStatusData: ValveStatusValidationDto | null = null;
  valveDescriptionsData: ValveDescriptionValidationDto | null = null;
  inactiveComponentsData: InactiveComponentsValidationDto | null = null;
  pipingClassStatusData: PipingClassStatusValidationDto | null = null;
  attributesData: ReleaseAttributesDto | null = null;

  // ── Release Attributes form ───────────────────────────────────────────
  attributesForm: FormGroup;
  /** True while the auto-save debounce call is in-flight. */
  savingAttributes$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  // ── Section expansion state ───────────────────────────────────────────
  valveStatusSections: Record<string, boolean> = { modify: false, releaseAsIs: false, retire: false };
  valveDescriptionSections: Record<string, boolean> = { modify: false, releaseAsIs: false, retire: false };
  pipingClassSections: Record<string, boolean> = { modify: false, nonReview: false, releaseAsIs: false, retired: false };

  // ── Valve Status: update-to-newer-version selection ───────────────────
  /** Enables selection and bulk updates to newer published valve versions. */
  readonly enableValveVersionUpdates: boolean = true;

  /** Visible columns for the Valve Status tables. */
  get valveStatusColumns(): string[] {
    const columns: string[] = this.enableValveVersionUpdates
      ? ['select', 'valveTag', 'pipeClasses', 'valveStatus', 'message']
      : ['valveTag', 'pipeClasses', 'valveStatus', 'message'];

    if (this.getAllValveStatusRows().some((row: ValveStatusRowDto) => this.canEnableValve(row))) {
      columns.push('action');
    }

    return columns;
  }

  /**
   * Valve IDs the user has selected to update to their latest published version.
   * The selection spans all three sections; only rows whose `canUpdateToLatestVersion` is true
   * can be selected.
   */
  selectedValveIdsForUpdate: Set<string> = new Set<string>();

  /** Original catalog component IDs selected for a release-wide bulk action. */
  selectedInactiveComponentIds: Set<string> = new Set<string>();

  /** Keys of valve-description rows selected for a bulk choice. */
  selectedValveDescriptionRowKeys: Set<string> = new Set<string>();

  /** Keeps the display rows stable so Angular does not recreate radio controls on every check. */
  private readonly valveDescriptionDisplayRowsCache: WeakMap<ValveDescriptionRowDto[], ValveDescriptionRowDto[]> = new WeakMap();

  /**
   * Index of the currently active tab (0 = General Details), kept in sync by `onTabChange`.
   * Used by the window-focus refresh handler to know which loader to re-invoke.
   */
  private currentTabIndex: number = 0;

  /** Tabs whose cached data predates the last focus refresh; refreshed in the background when first revisited. */
  private readonly staleTabIndexes: Set<number> = new Set<number>();

  // ── Private state ─────────────────────────────────────────────────────
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly router: Router = inject(Router);
  private readonly apiClient: EsraPipingAPIClient = inject(EsraPipingAPIClient);
  private readonly snackBar: SnackBarService = inject(SnackBarService);
  private readonly msalService: MsalService = inject(MsalService);
  private readonly dialog: MatDialog = inject(MatDialog);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly _formBuilder: FormBuilder = inject(FormBuilder);
  private readonly exportService: ReleaseFinalChecksExportService = inject(ReleaseFinalChecksExportService);
  private readonly downloadService: DownloadService = inject(DownloadService);
  private readonly graphService: GraphService = inject(GraphService);
  private readonly userService: UserService = inject(UserService);

  /**
   * Cache of resolved `oid -> display name` for users who have replaced an inactive component or
   * valve slot. Populated lazily as tab data loads / rows are replaced so the "Replaced By" column
   * can show a readable name instead of a raw GUID.
   */
  private readonly replacedByDisplayNames: Map<string, string> = new Map<string, string>();
  /** OIDs currently being resolved through Microsoft Graph; prevents showing raw GUIDs while loading. */
  private readonly resolvingDecidedByOids: Set<string> = new Set<string>();

  constructor() {
    this.attributesForm = this._formBuilder.group({
      name: ['', Validators.required],
      revision: ['', Validators.required],
      description: [''],
    });
  }

  ngOnInit(): void {
    this.releaseId = this.route.snapshot.paramMap.get('releaseId') ?? '';
    this.canEnableValves = this.userService.isRoleExists(Constants.admin)
      || this.userService.isRoleExists(Constants.pipingDictionaryWriter);
    this.loadSummary();
    this.loadReleasePermissions();
    // Load attributes eagerly so General Details tab can display them without
    // requiring the user to visit the Release Attributes tab first.
    this.loadAttributes();

    // Auto-save attributes on form change with 500ms debounce.
    this.attributesForm.valueChanges.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      if (this.attributesForm.valid && this.attributesData) {
        this.autoSaveAttributes();
      }
    });

    // Pipe classes and valves referenced by this release are usually edited by opening
    // them in a NEW browser tab (see openPipeClassInNewTab / row.navigationId links), while
    // this release page stays alive in the background with its tab data already cached
    // in memory (see the `if (this.xxxData) { return; }` guards in the loaders below).
    // When the user comes back to this tab, refresh the summary and the currently active
    // tab so edits made in the other tab (e.g. reactivating a component from the Pipe
    // Class editor) are reflected instead of showing stale cached data.
    window.addEventListener('focus', this.onWindowFocus);
    this.destroyRef.onDestroy(() => window.removeEventListener('focus', this.onWindowFocus));
  }

  /**
   * Refetches the summary and the active tab in the background on window focus. The data on screen is
   * never cleared and no loading state hides it: the new response replaces it in place (and only when it
   * actually changed), so there is no flicker and the scroll position is kept. The other tabs are marked
   * stale and refreshed the same way the first time they are opened again.
   * Bound as an instance property (not a prototype method) so it can be added/removed as the
   * same function reference with `addEventListener`/`removeEventListener`.
   *
   * The in-progress bulk-update checkbox selection is preserved across the refresh (see `loadValveStatus`);
   * the "proceed without updates" toggle is persisted server-side, so re-fetching only ever shows its
   * correct current value.
   */
  private readonly onWindowFocus = (): void => {
    for (const tabIndex of [0, 1, 2, 3, 4, 5]) {
      if (tabIndex !== this.currentTabIndex) { this.staleTabIndexes.add(tabIndex); }
    }

    // Rapid focus changes must not stack duplicate requests; the one in flight already carries fresh data.
    if (this.refreshCounter.getValue() > 0) { return; }

    this.loadSummary(true);
    this.loadAttributes(true);
    this.loadReleasePermissions();
    this.onTabChange(this.currentTabIndex, true);
  };


  /** Returns the OID of the currently authenticated user. */
  private getCurrentUserOid(): string {
    return this.msalService.instance.getActiveAccount()?.idTokenClaims?.['oid'] as string ?? '';
  }

  /**
   * Loads the release detail solely to determine whether the current user is the
   * release author or an owner, mirroring the same check used on the release
   * details page so the Release button is consistently hidden for other users.
   */
  private loadReleasePermissions(): void {
    this.apiClient.getReleaseById(this.releaseId).pipe(
      retryTransient<ReleaseDetailViewModel>(),
      catchError(() => EMPTY),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((release: ReleaseDetailViewModel) => {
      this.releaseStatus = release.status;

      const currentOid: string = this.getCurrentUserOid();
      if (!currentOid) {
        this.canManageRelease = false;
        return;
      }

      if (release.authorOid && release.authorOid === currentOid) {
        this.canManageRelease = true;
        return;
      }

      const roles: ReleaseRoleViewModel[] = release.roles ?? [];
      this.canManageRelease = roles.some(
        (role: ReleaseRoleViewModel) => role.userRole === UserType._1 && role.userId === currentOid
      );
    });
  }

  // ── Summary ───────────────────────────────────────────────────────────

  /**
   * Fetches the summary DTO and refreshes all tab badge states.
   * @param background When true the response replaces the current summary silently (no content-hiding spinner).
   */
  loadSummary(background: boolean = false): void {
    this.apiClient.getSummary(this.releaseId).pipe(
      this.trackLoading(background, this.loadingSummary$),
      catchError(err => {
        if (!background) { this.snackBar.showError(getApiErrorMessage(err, 'Failed to load final checks summary.')); }
        return EMPTY;
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(summary => {
      this.summary = this.keepIfUnchanged(this.summary, summary);
    });
  }

  /**
   * Foreground loads hide the content behind a spinner; background refreshes only show the small header spinner
   * and retry transient failures, keeping the spinner on for the whole retry window.
   */
  private trackLoading<T>(background: boolean, foregroundIndicator: Subject<boolean>): MonoTypeOperatorFunction<T> {
    return background
      ? (source: Observable<T>): Observable<T> => source.pipe(retryTransient<T>(), indicateWithCounter<T>(this.refreshing$, this.refreshCounter))
      : indicate<T>(foregroundIndicator);
  }

  /** Returns `current` when `next` carries the same data, so an unchanged refresh does not re-render the view. */
  private keepIfUnchanged<T>(current: T | null, next: T): T {
    return current !== null && JSON.stringify(current) === JSON.stringify(next) ? current : next;
  }

  /** Returns the badge state object for a given tab section summary. */
  getBadgeState(section: FinalCheckSectionSummary | undefined): 'error' | 'warning' | 'ok' {
    if (!section) { return 'ok'; }
    if (section.hasBlockingErrors) { return 'error'; }
    if (section.hasUnacknowledgedWarnings) { return 'warning'; }
    return 'ok';
  }

  /**
   * Returns the badge state for the Valve Status tab label.
   * Before the tab data has loaded, falls back to the backend summary flags. Once loaded,
   * derives the state directly from the rows across all sections (Revise, Release As-Is,
   * Retire) so the icon reflects every valve the user can actually see, not just the
   * backend's blocking/unacknowledged-warning aggregates.
   */
  getValveStatusTabBadgeState(): 'error' | 'warning' | 'ok' {
    if (!this.valveStatusData || this.staleTabIndexes.has(ReleaseFinalChecksComponent.VALVE_STATUS_TAB_INDEX)) {
      return this.getBadgeState(this.summary?.valveStatus);
    }
    const rows: ValveStatusRowDto[] = this.getAllValveStatusRows();
    if (rows.some(row => row.severity === ValidationSeverity._3)) { return 'error'; }
    if (rows.some(row => row.requiresUserConfirmation === true && row.isAcknowledgedByUser !== true)) { return 'warning'; }
    return 'ok';
  }

  // ── Tab data loaders ──────────────────────────────────────────────────

  /**
   * Called when the user selects a tab; loads the corresponding tab data if not yet loaded, or
   * refreshes it in the background when it went stale while the window was out of focus.
   * @param background True for a window-focus refresh of the active tab.
   */
  onTabChange(tabIndex: number, background: boolean = false): void {
    this.currentTabIndex = tabIndex;
    const refreshInBackground: boolean = background || this.staleTabIndexes.delete(tabIndex);
    switch (tabIndex) {
      case 1: this.loadValveStatus(refreshInBackground); break;
      case 2: this.loadValveDescriptions(refreshInBackground); break;
      case 3: this.loadInactiveComponents(refreshInBackground); break;
      case 4: this.loadPipingClassStatus(refreshInBackground); break;
      case 5: this.loadAttributes(refreshInBackground); break;
      default: break;
    }
  }

  loadValveStatus(background: boolean = false): void {
    if (!background && this.valveStatusData) { return; }
    this.apiClient.getValveStatus(this.releaseId).pipe(
      this.trackLoading(background, this.loadingTab$),
      catchError(err => {
        if (!background) { this.snackBar.showError(getApiErrorMessage(err, 'Failed to load valve status.')); }
        return EMPTY;
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(data => {
      // Sort each section by severity (most critical first), then by valve tag.
      data.modifyClassValves = this.sortValveStatusRows(data.modifyClassValves);
      data.releaseAsIsClassValves = this.sortValveStatusRows(data.releaseAsIsClassValves);
      data.retireClassValves = this.sortValveStatusRows(data.retireClassValves);
      this.valveStatusData = this.keepIfUnchanged(this.valveStatusData, data);

      // Drop selections for valves a refresh (e.g. on window focus) revealed as no longer
      // eligible -- already updated, no longer referenced, or superseded by yet another version.
      const stillEligibleIds: Set<string> = new Set<string>(
        this.getAllValveStatusRows().filter(row => this.canUpdateValveToLatest(row) && row.valveId).map(row => row.valveId as string),
      );
      for (const valveId of this.selectedValveIdsForUpdate) {
        if (!stillEligibleIds.has(valveId)) { this.selectedValveIdsForUpdate.delete(valveId); }
      }
    });
  }

  loadValveDescriptions(background: boolean = false, notifyWhenAllResolved: boolean = false): void {
    if (!background && this.valveDescriptionsData) { return; }
    // Piping Class Status provides one display name per pipe class, unlike Valve Status which
    // can consolidate multiple pipe classes that reference the same valve.
    this.loadPipingClassStatus(background);
    this.apiClient.getValveDescriptions(this.releaseId).pipe(
      this.trackLoading(background, this.loadingTab$),
      catchError(err => {
        if (!background) { this.snackBar.showError(getApiErrorMessage(err, 'Failed to load valve descriptions.')); }
        return EMPTY;
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(data => {
      this.valveDescriptionsData = this.keepIfUnchanged(this.valveDescriptionsData, data);
      this.pruneValveDescriptionSelection();
      if (notifyWhenAllResolved && !this.hasUnresolvedValveDescriptionConflicts()) {
        this.snackBar.showInfo('All valve description conflicts are resolved.');
      }
    });
  }

  loadInactiveComponents(background: boolean = false): void {
    if (!background && this.inactiveComponentsData) { return; }
    // Piping Class Status data supplies the per-pipe-class release action (Revise/Retire/Release
    // As-Is) needed to decide whether the resolution buttons should be shown for a component
    // group (see `shouldShowInactiveComponentActions`).
    this.loadPipingClassStatus(background);
    this.apiClient.getInactiveComponents(this.releaseId).pipe(
      this.trackLoading(background, this.loadingTab$),
      catchError(err => {
        if (!background) { this.snackBar.showError(getApiErrorMessage(err, 'Failed to load inactive components.')); }
        return EMPTY;
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(data => {
      this.inactiveComponentsData = this.keepIfUnchanged(this.inactiveComponentsData, data);
      this.resolveDecidedByNames(data.components ?? []);
    });
  }

  loadPipingClassStatus(background: boolean = false): void {
    if (!background && this.pipingClassStatusData) { return; }
    this.apiClient.getPipingClassStatus(this.releaseId).pipe(
      this.trackLoading(background, this.loadingTab$),
      catchError(err => {
        if (!background) { this.snackBar.showError(getApiErrorMessage(err, 'Failed to load piping class status.')); }
        return EMPTY;
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(data => {
      this.pipingClassStatusData = this.keepIfUnchanged(this.pipingClassStatusData, data);
    });
  }

  loadAttributes(background: boolean = false): void {
    if (!background && this.attributesData) { return; }
    this.apiClient.getReleaseAttributes(this.releaseId).pipe(
      this.trackLoading(background, this.loadingTab$),
      catchError(err => {
        if (!background) { this.snackBar.showError(getApiErrorMessage(err, 'Failed to load release attributes.')); }
        return EMPTY;
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(data => {
      // A background refresh must not overwrite what the user is typing in the attributes form.
      if (background && this.attributesForm.dirty) { return; }

      this.attributesData = this.keepIfUnchanged(this.attributesData, data);
      this.attributesForm.patchValue({
        name: data.name ?? '',
        revision: data.revision ?? '',
        description: data.description ?? '',
      }, { emitEvent: false });
    });
  }

  // ── Acknowledge ───────────────────────────────────────────────────────

  /** Resolves one valve-description row and replaces the table with the server response. */
  saveValveDescriptionChoice(row: ValveDescriptionRowDto, option: ValveDescriptionChoiceOption): void {
    const renderedRow: ValveDescriptionRowDto | undefined = this.getValveDescriptionRows()
      .find(currentRow => this.getValveDescriptionRowKey(currentRow) === this.getValveDescriptionRowKey(row));
    if (!renderedRow || !this.isValveDescriptionEditable(renderedRow)) { return; }

    const dto: ValveDescriptionChoiceRequestDto = new ValveDescriptionChoiceRequestDto({
      valveId: renderedRow.valveId,
      pipeClassIds: renderedRow.pipeClassIds ?? [],
      chosenOption: option,
      actorOid: this.getCurrentUserOid(),
    });

    this.apiClient.saveValveDescriptionChoice(this.releaseId, dto).pipe(
      indicate(this.savingDescriptionChoice$),
      catchError(err => {
        this.snackBar.showError(getApiErrorMessage(err, 'Failed to save description choice.'));
        return EMPTY;
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(data => {
      this.valveDescriptionsData = data;
      this.pruneValveDescriptionSelection();
      this.snackBar.showInfo('Description choice saved.');
      this.loadSummary();
    });
  }

  getValveDescriptionRows(): ValveDescriptionRowDto[] {
    return [
      ...this.getPrioritizedValveDescriptionRows(this.valveDescriptionsData?.modifyClassValves),
      ...this.getPrioritizedValveDescriptionRows(this.valveDescriptionsData?.releaseAsIsClassValves),
      ...this.getPrioritizedValveDescriptionRows(this.valveDescriptionsData?.retireClassValves),
    ];
  }

  getPrioritizedValveDescriptionRows(rows: ValveDescriptionRowDto[] | undefined): ValveDescriptionRowDto[] {
    if (!rows) { return []; }

    const cachedRows: ValveDescriptionRowDto[] | undefined = this.valveDescriptionDisplayRowsCache.get(rows);
    if (cachedRows) { return cachedRows; }

    const descriptionRows: ValveDescriptionRowDto[] = rows.flatMap(row =>
      (row.pipeClassIds?.length ? row.pipeClassIds : [row.pipeClassId]).map(pipeClassId =>
        new ValveDescriptionRowDto({ ...row, pipeClassId })
      )
    );
    const prioritizedRows: ValveDescriptionRowDto[] = [
      ...descriptionRows.filter(row => this.hasDescriptionDifference(row)),
      ...descriptionRows.filter(row => !this.hasDescriptionDifference(row)),
    ];
    this.valveDescriptionDisplayRowsCache.set(rows, prioritizedRows);
    return prioritizedRows;
  }

  getValveDescriptionRowKey(row: ValveDescriptionRowDto): string {
    return [this.getValveDescriptionGroupKey(row), row.pipeClassId ?? ''].join('|');
  }

  private getValveDescriptionGroupKey(row: ValveDescriptionRowDto): string {
    return [row.valveId ?? '', ...(row.pipeClassIds ?? []), row.oldValue ?? '', row.newValue ?? ''].join('|');
  }

  isValveDescriptionRowSelected(row: ValveDescriptionRowDto): boolean {
    return this.selectedValveDescriptionRowKeys.has(this.getValveDescriptionRowKey(row));
  }

  /** Returns true when there are unresolved, editable rows currently displayed in the tab. */
  hasSelectableValveDescriptionRows(): boolean {
    return this.getDisplayedEditableValveDescriptionRows().length > 0;
  }

  /** Returns true when every displayed editable row is selected. */
  isAllDisplayedValveDescriptionRowsSelected(): boolean {
    const displayedRows: ValveDescriptionRowDto[] = this.getDisplayedEditableValveDescriptionRows();
    if (displayedRows.length === 0) { return false; }
    return displayedRows.every((row: ValveDescriptionRowDto) => this.isValveDescriptionRowSelected(row));
  }

  /** Returns true when only a subset of displayed editable rows is selected. */
  isValveDescriptionSelectAllIndeterminate(): boolean {
    const displayedRows: ValveDescriptionRowDto[] = this.getDisplayedEditableValveDescriptionRows();
    if (displayedRows.length === 0) { return false; }
    const selectedVisibleCount: number = displayedRows
      .filter((row: ValveDescriptionRowDto) => this.isValveDescriptionRowSelected(row))
      .length;
    return selectedVisibleCount > 0 && selectedVisibleCount < displayedRows.length;
  }

  /** Selects or unselects every currently displayed editable mismatch row. */
  toggleAllDisplayedValveDescriptionRows(selected: boolean): void {
    const displayedRows: ValveDescriptionRowDto[] = this.getDisplayedEditableValveDescriptionRows();
    for (const row of displayedRows) {
      const rowKey: string = this.getValveDescriptionRowKey(row);
      if (selected) {
        this.selectedValveDescriptionRowKeys.add(rowKey);
      } else {
        this.selectedValveDescriptionRowKeys.delete(rowKey);
      }
    }
  }

  toggleValveDescriptionRow(row: ValveDescriptionRowDto, selected: boolean): void {
    if (!this.isValveDescriptionEditable(row)) { return; }

    const key: string = this.getValveDescriptionRowKey(row);
    if (selected) {
      this.selectedValveDescriptionRowKeys.add(key);
    } else {
      this.selectedValveDescriptionRowKeys.delete(key);
    }
  }

  applyValveDescriptionChoiceToSelected(option: ValveDescriptionChoiceOption): void {
    const selectedRows: ValveDescriptionRowDto[] = this.getValveDescriptionRows()
      .filter(row => this.isValveDescriptionEditable(row) && this.isValveDescriptionRowSelected(row));
    if (selectedRows.length === 0) { return; }

    this.confirmValveDescriptionBulkChoice(option, selectedRows.length, () =>
      this.saveValveDescriptionChoicesForSelectedRows(selectedRows, option));
  }

  private saveValveDescriptionChoicesForSelectedRows(rows: ValveDescriptionRowDto[], option: ValveDescriptionChoiceOption): void {
    const selectedRowKeys: string[] = rows.map(row => this.getValveDescriptionRowKey(row));
    from(selectedRowKeys).pipe(
      concatMap(rowKey => {
        const renderedRow: ValveDescriptionRowDto | undefined = this.getValveDescriptionRows()
          .find(row => this.getValveDescriptionRowKey(row) === rowKey);
        if (!renderedRow || !this.isValveDescriptionEditable(renderedRow)) { return EMPTY; }

        return this.apiClient.saveValveDescriptionChoice(this.releaseId,
          new ValveDescriptionChoiceRequestDto({
          valveId: renderedRow.valveId,
          pipeClassIds: renderedRow.pipeClassIds ?? [],
          chosenOption: option,
          actorOid: this.getCurrentUserOid(),
          })).pipe(tap(data => this.valveDescriptionsData = data));
      }),
      toArray(),
      indicate(this.savingDescriptionChoice$),
      catchError(err => {
        this.snackBar.showError(getApiErrorMessage(err, 'Failed to save description choices.'));
        return EMPTY;
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      this.refreshValveDescriptionsAfterBulkAction();
    });
  }

  /**
   * Only unresolved mismatches from pipe classes being revised in the current release can be
   * resolved. A persisted chosenOption intentionally does not disable the controls: the user may
   * change a previous decision while the backend continues to report a mismatch.
   */
  isValveDescriptionEditable(row: ValveDescriptionRowDto): boolean {
    const hasUnresolvedMismatch: boolean = row.descriptionMismatch === true;
    if (!hasUnresolvedMismatch) { return false; }

    if (row.pipeClassAction != null) {
      return row.pipeClassAction === ReleaseSelectedObject._1;
    }

    return (this.valveDescriptionsData?.modifyClassValves ?? []).some(sourceRow =>
      this.getValveDescriptionGroupKey(sourceRow) === this.getValveDescriptionGroupKey(row)
    );
  }

  /** Resolves the display name for every consolidated pipe class in a description row. */
  getPipeClassesForValveDescription(row: ValveDescriptionRowDto): string {
    if (!this.pipingClassStatusData) {
      return 'Loading pipe classes...';
    }

    const pipeClassRows: PipingClassStatusRowDto[] = [
      ...(this.pipingClassStatusData.modifyClasses ?? []),
      ...(this.pipingClassStatusData.nonReviewClasses ?? []),
      ...(this.pipingClassStatusData.releaseAsIsClasses ?? []),
      ...(this.pipingClassStatusData.retiredClasses ?? []),
    ];

    const pipeClassId: string | undefined = row.pipeClassId;
    if (!pipeClassId) { return 'Pipe class unavailable'; }

    return pipeClassRows.find(pipeClassRow =>
      pipeClassRow.pipeClassId === pipeClassId || pipeClassRow.pipeClassCloneId === pipeClassId
    )?.pipeClassName
      ?? 'Pipe class unavailable';
  }

  applyValveDescriptionChoiceToAll(option: ValveDescriptionChoiceOption): void {
    this.confirmValveDescriptionBulkChoice(option, null, () => this.saveValveDescriptionChoiceToAll(option));
  }

  private saveValveDescriptionChoiceToAll(option: ValveDescriptionChoiceOption): void {
    this.apiClient.saveAllValveDescriptionChoices(this.releaseId,
      new ValveDescriptionBulkChoiceRequestDto({ chosenOption: option, actorOid: this.getCurrentUserOid() })).pipe(
      indicate(this.savingDescriptionChoice$),
      catchError(err => {
        this.snackBar.showError(getApiErrorMessage(err, 'Failed to save description choices.'));
        return EMPTY;
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(data => {
      this.valveDescriptionsData = data;
      this.refreshValveDescriptionsAfterBulkAction();
    });
  }

  /** Returns the rows currently displayed in the tab that can still be resolved by the user. */
  private getDisplayedEditableValveDescriptionRows(): ValveDescriptionRowDto[] {
    return this.getValveDescriptionRows().filter((row: ValveDescriptionRowDto) => this.isValveDescriptionEditable(row));
  }

  /** Removes selected keys that are no longer visible/editable after refresh or resolution. */
  private pruneValveDescriptionSelection(): void {
    const validRowKeys: Set<string> = new Set<string>(
      this.getDisplayedEditableValveDescriptionRows().map((row: ValveDescriptionRowDto) => this.getValveDescriptionRowKey(row)),
    );
    for (const selectedRowKey of this.selectedValveDescriptionRowKeys) {
      if (!validRowKeys.has(selectedRowKey)) {
        this.selectedValveDescriptionRowKeys.delete(selectedRowKey);
      }
    }
  }

  /** Refreshes Valve Descriptions from GET so resolved conflicts disappear immediately. */
  private refreshValveDescriptionsAfterBulkAction(): void {
    this.loadValveDescriptions(true);
    this.loadSummary(true);
  }

  private confirmValveDescriptionBulkChoice(option: ValveDescriptionChoiceOption, selectedCount: number | null, onConfirm: () => void): void {
    const action: string = option === ValveDescriptionChoiceOption._0 ? 'keep the current descriptions' : 'use the new descriptions';
    const scope: string = selectedCount == null ? 'all pending description rows' : `${selectedCount} selected description row${selectedCount === 1 ? '' : 's'}`;
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '450px',
      data: {
        dialogMessage: `This will ${action} for ${scope}. Do you want to continue?`,
        confirmText: 'Continue',
        cancelText: 'Cancel',
      },
    });

    dialogRef.afterClosed().pipe(
      first(),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(confirmed => {
      if (confirmed) { onConfirm(); }
    });
  }

  // ── Inactive Components: Replace / Activate / Release As-Is (AC1, AC7) ─

  /**
   * Opens the component-catalog modal used by the revisions add-component flow, pre-filtered to
   * the inactive component's group, so the user picks a replacement from the full filterable/
   * searchable catalog grid. On confirmation, calls the replace API. The action and its
   * replacement apply release-wide across every pipe-class occurrence of this component (AC7).
   */
  openReplaceComponentDialog(group: InactiveComponentGroupDto): void {
    this.apiClient.getComponentById(group.componentId ?? '').pipe(
      catchError(err => {
        this.snackBar.showError(getApiErrorMessage(err, 'Failed to load component details.'));
        return EMPTY;
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(inactiveComponent => {
      const dialogData: ComponentCatalogSelectionData = {
        title: `Replace ${group.componentCode ?? group.description ?? 'Component'}`,
        groupFilter: inactiveComponent.group ?? '',
      };

      const dialogRef = this.dialog.open<ComponentCatalogModalComponent, ComponentCatalogSelectionData, ComponentOption[]>(
        ComponentCatalogModalComponent,
        {
          width: '95vw',
          maxWidth: '2000px',
          height: '95vh',
          maxHeight: '98vh',
          panelClass: 'xlarge-component-catalog-modal',
          data: dialogData,
        },
      );

      dialogRef.afterClosed().pipe(
        first(),
        takeUntilDestroyed(this.destroyRef),
      ).subscribe(selectedComponents => {
        const replacementComponentId: string | undefined = selectedComponents?.[0]?.id;
        if (!replacementComponentId) { return; }
        this.saveComponentReplacement(group, replacementComponentId);
      });
    });
  }

  /** Calls the replace API for the inactive component group and splices the updated group in-place. */
  private saveComponentReplacement(group: InactiveComponentGroupDto, replacementComponentId: string): void {
    const dto: ComponentReplacementRequestDto = new ComponentReplacementRequestDto({
      componentId: group.componentId,
      replacementComponentId: replacementComponentId,
      actorOid: this.getCurrentUserOid(),
    });
    this.apiClient.replaceInactiveComponent(this.releaseId, dto).pipe(
      indicate(this.resolvingInactiveComponent$),
      catchError(err => {
        this.handleInactiveComponentActionError(err, 'Failed to replace component.');
        return EMPTY;
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(updatedGroup => {
      this.replaceInactiveComponentGroupInPlace(updatedGroup);
      this.loadSummary();
    });
  }

  /**
   * Opens a confirmation dialog before reactivating an inactive component. Applies release-wide
   * across every pipe-class occurrence (AC7).
   */
  openActivateComponentDialog(group: InactiveComponentGroupDto): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '450px',
      data: {
        dialogMessage: `Activate ${group.componentCode ?? group.description ?? 'this component'}? This will mark it as active across all pipe classes in this release.`,
        confirmText: 'Activate',
        cancelText: 'Cancel',
      },
    });

    dialogRef.afterClosed().pipe(
      first(),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(confirmed => {
      if (!confirmed) { return; }
      this.saveComponentActivation(group);
    });
  }

  /** Calls the activate API for the inactive component group and splices the updated group in-place. */
  private saveComponentActivation(group: InactiveComponentGroupDto): void {
    const dto: ComponentActivationRequestDto = new ComponentActivationRequestDto({
      componentId: group.componentId,
      actorOid: this.getCurrentUserOid(),
    });
    this.apiClient.activateInactiveComponent(this.releaseId, dto).pipe(
      indicate(this.resolvingInactiveComponent$),
      catchError(err => {
        this.handleInactiveComponentActionError(err, 'Failed to activate component.');
        return EMPTY;
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(updatedGroup => {
      this.replaceInactiveComponentGroupInPlace(updatedGroup);
      this.loadSummary();
    });
  }

  /**
   * Opens a confirmation dialog before releasing an inactive component as-is, without replacing
   * or activating it. Applies release-wide across every pipe-class occurrence (AC7).
   */
  openReleaseAsIsDialog(group: InactiveComponentGroupDto): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '450px',
      data: {
        dialogMessage: `Release ${group.componentCode ?? group.description ?? 'this component'} as-is, without replacing or activating it?`,
        confirmText: 'Release As-Is',
        cancelText: 'Cancel',
      },
    });

    dialogRef.afterClosed().pipe(
      first(),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(confirmed => {
      if (!confirmed) { return; }
      this.saveReleaseAsIs(group);
    });
  }

  /** Calls the release-as-is API for the inactive component group and splices the updated group in-place. */
  private saveReleaseAsIs(group: InactiveComponentGroupDto): void {
    const dto: ComponentReleaseAsIsRequestDto = new ComponentReleaseAsIsRequestDto({
      componentId: group.componentId,
      actorOid: this.getCurrentUserOid(),
    });
    this.apiClient.releaseInactiveComponentAsIs(this.releaseId, dto).pipe(
      indicate(this.resolvingInactiveComponent$),
      catchError(err => {
        this.handleInactiveComponentActionError(err, 'Failed to release component as-is.');
        return EMPTY;
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(updatedGroup => {
      this.replaceInactiveComponentGroupInPlace(updatedGroup);
      this.loadSummary();
    });
  }

  /**
   * Handles a failed Replace/Activate/Release-As-Is call for an inactive component group.
   * The backend now returns 404 (`EntityNotFoundException`) when the target pipe class's
   * revision is no longer in `Revise` (e.g. it was changed to Release As-Is/Retire, or the
   * component was already resolved, in another browser tab/session). In that case the FE's
   * cached Inactive Components tab is stale, so it is invalidated and reloaded from the
   * server instead of showing a generic error — this also removes/updates the row so the
   * user cannot retry the same now-invalid action.
   * @param err The error thrown by the API call.
   * @param fallbackMessage Generic message shown when the error is not a 404.
   */
  private handleInactiveComponentActionError(err: unknown, fallbackMessage: string): void {
    const isConflict: boolean = err instanceof ApiException && err.status === 409;
    if (isConflict) {
      this.snackBar.showError('Inactive component actions are unavailable while Proceed without updates is enabled. Refreshing data…');
      this.reloadInactiveComponentsAndSummary();
      return;
    }
    const isNotFound: boolean = err instanceof ApiException && err.status === 404;
    if (isNotFound) {
      this.snackBar.showError('This component could not be found or is no longer available for this action. Refreshing data…');
      this.inactiveComponentsData = null;
      this.pipingClassStatusData = null;
      this.loadInactiveComponents();
      this.loadSummary();
      return;
    }
    this.snackBar.showError(getApiErrorMessage(err, fallbackMessage));
  }

  // ── Inactive Components: release-wide acknowledgement and bulk actions ──

  /** The persisted API state is the sole source of truth for whether individual/bulk actions are unavailable. */
  isInactiveComponentsProceedWithoutUpdatesEnabled(): boolean {
    return this.inactiveComponentsData?.proceedWithoutUpdates === true;
  }

  /** Returns true when an unresolved component group can be included in a bulk action. */
  canSelectInactiveComponent(group: InactiveComponentGroupDto): boolean {
    return !this.isInactiveComponentsProceedWithoutUpdatesEnabled()
      && this.isInactiveComponentPending(group)
      && this.shouldShowInactiveComponentActions(group)
      && !!group.componentId;
  }

  toggleInactiveComponentSelection(group: InactiveComponentGroupDto): void {
    if (!this.canSelectInactiveComponent(group) || !group.componentId) { return; }
    if (this.selectedInactiveComponentIds.has(group.componentId)) {
      this.selectedInactiveComponentIds.delete(group.componentId);
    } else {
      this.selectedInactiveComponentIds.add(group.componentId);
    }
  }

  toggleAllInactiveComponents(checked: boolean): void {
    this.selectedInactiveComponentIds.clear();
    if (!checked) { return; }
    for (const group of this.inactiveComponentsData?.components ?? []) {
      if (this.canSelectInactiveComponent(group) && group.componentId) {
        this.selectedInactiveComponentIds.add(group.componentId);
      }
    }
  }

  getSelectableInactiveComponentCount(): number {
    return (this.inactiveComponentsData?.components ?? []).filter(group => this.canSelectInactiveComponent(group)).length;
  }

  onInactiveComponentsProceedWithoutUpdatesChange(checked: boolean, toggle: MatSlideToggle): void {
    if (!checked) {
      this.saveInactiveComponentsProceedWithoutUpdates(false);
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '520px',
      data: {
        dialogMessage: 'Proceed without updates. All inactive component conflicts will be acknowledged for this release.',
        confirmText: 'Proceed without updates',
        cancelText: 'Cancel',
      },
    });
    dialogRef.afterClosed().pipe(first(), takeUntilDestroyed(this.destroyRef)).subscribe(confirmed => {
      if (!confirmed) {
        toggle.checked = false;
        return;
      }
      this.saveInactiveComponentsProceedWithoutUpdates(true);
    });
  }

  private saveInactiveComponentsProceedWithoutUpdates(acknowledged: boolean): void {
    const dto: InactiveComponentProceedWithoutUpdatesRequestDto = new InactiveComponentProceedWithoutUpdatesRequestDto({
      acknowledged,
      actorOid: this.getCurrentUserOid(),
      notes: undefined,
    });
    this.apiClient.setInactiveComponentsProceedWithoutUpdates(this.releaseId, dto).pipe(
      indicate(this.savingInactiveComponentsProceedWithoutUpdates$),
      catchError(err => {
        this.snackBar.showError(getApiErrorMessage(err, 'Failed to save the acknowledgement.'));
        this.reloadInactiveComponentsAndSummary();
        return EMPTY;
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(refreshed => this.applyRefreshedInactiveComponents(refreshed));
  }

  openBulkReleaseInactiveComponentsAsIsDialog(): void {
    this.openInactiveComponentBulkConfirmation(
      'Release the selected components as-is across their occurrences in this release?',
      'Release As-Is',
      () => this.apiClient.bulkReleaseInactiveComponentsAsIs(this.releaseId, this.createBulkInactiveComponentsRequest()),
    );
  }

  openBulkActivateInactiveComponentsDialog(): void {
    this.openInactiveComponentBulkConfirmation(
      'Activate the selected components across every occurrence in this release’s Revise pipe classes?',
      'Activate selected',
      () => this.apiClient.bulkActivateInactiveComponents(this.releaseId, this.createBulkInactiveComponentsRequest()),
    );
  }

  openBulkRemoveInactiveComponentsDialog(): void {
    this.openInactiveComponentBulkConfirmation(
      'Remove the selected components from every occurrence in this release’s Revise pipe classes? The components remain in the catalog; only their release revision clones are removed.',
      'Remove from revision',
      () => this.apiClient.bulkRemoveInactiveComponents(this.releaseId, this.createBulkInactiveComponentsRequest()),
    );
  }

  /** Confirms then removes one component from all of its Revise pipe-class release clones. */
  openRemoveInactiveComponentDialog(group: InactiveComponentGroupDto): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '560px',
      data: {
        dialogMessage: `Remove ${group.componentCode ?? group.description ?? 'this component'} from this release's Revise pipe-class revisions? It affects every Revise pipe class in this release containing the component. The component is not deleted from the catalog.`,
        confirmText: 'Remove from revision',
        cancelText: 'Cancel',
      },
    });
    dialogRef.afterClosed().pipe(first(), takeUntilDestroyed(this.destroyRef)).subscribe(confirmed => {
      if (!confirmed) { return; }
      const dto: ComponentRemovalRequestDto = new ComponentRemovalRequestDto({
        componentId: group.componentId,
        actorOid: this.getCurrentUserOid(),
      });
      this.apiClient.removeInactiveComponent(this.releaseId, dto).pipe(
        indicate(this.resolvingInactiveComponent$),
        catchError(err => {
          this.handleInactiveComponentActionError(err, 'Failed to remove component from the release revision.');
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
      ).subscribe(refreshed => this.applyRefreshedInactiveComponents(refreshed));
    });
  }

  private openInactiveComponentBulkConfirmation(
    dialogMessage: string,
    confirmText: string,
    request: () => Observable<InactiveComponentsValidationDto>,
  ): void {
    if (this.selectedInactiveComponentIds.size === 0) { return; }
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '560px',
      data: { dialogMessage, confirmText, cancelText: 'Cancel' },
    });
    dialogRef.afterClosed().pipe(first(), takeUntilDestroyed(this.destroyRef)).subscribe(confirmed => {
      if (!confirmed) { return; }
      request().pipe(
        indicate(this.applyingInactiveComponentBulkAction$),
        catchError(err => {
          this.handleInactiveComponentActionError(err, `Failed to ${confirmText.toLowerCase()} components.`);
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
      ).subscribe(refreshed => this.applyRefreshedInactiveComponents(refreshed));
    });
  }

  private createBulkInactiveComponentsRequest(): BulkInactiveComponentRequestDto {
    return new BulkInactiveComponentRequestDto({
      // The BE contract deliberately requires catalog component IDs, not componentInPipeClass IDs.
      componentIds: Array.from(this.selectedInactiveComponentIds),
      actorOid: this.getCurrentUserOid(),
    });
  }

  /** Replaces the complete tab state returned by a bulk/toggle request and refreshes publish eligibility. */
  private applyRefreshedInactiveComponents(refreshed: InactiveComponentsValidationDto): void {
    this.inactiveComponentsData = refreshed;
    this.selectedInactiveComponentIds.clear();
    this.resolveDecidedByNames(refreshed.components ?? []);
    this.loadSummary();
  }

  private reloadInactiveComponentsAndSummary(): void {
    this.selectedInactiveComponentIds.clear();
    this.inactiveComponentsData = null;
    this.pipingClassStatusData = null;
    this.loadInactiveComponents();
    this.loadSummary();
  }

  /**
   * Splices an updated component group into `inactiveComponentsData.components` in-place,
   * using a new array reference so change detection picks up the update immediately.
   */
  private replaceInactiveComponentGroupInPlace(updatedGroup: InactiveComponentGroupDto): void {
    if (!this.inactiveComponentsData?.components) { return; }
    const idx: number = this.inactiveComponentsData.components.findIndex(
      g => g.componentId === updatedGroup.componentId,
    );
    if (idx >= 0) {
      const newComponents: InactiveComponentGroupDto[] = [...this.inactiveComponentsData.components];
      newComponents[idx] = updatedGroup;
      this.inactiveComponentsData.components = newComponents;
      this.resolveDecidedByNames([updatedGroup]);
    }
  }

  /**
   * Resolves `decidedByOid` on every resolved component group to a display name via Graph,
   * caching results in `replacedByDisplayNames` so `getDecidedByDisplayName()` can render them
   * synchronously in the template. Called after the initial tab load and after each action,
   * since resolving introduces a new `decidedByOid` that may not be cached yet.
   */
  private resolveDecidedByNames(groups: InactiveComponentGroupDto[]): void {
    const oidsToResolve: Set<string> = new Set<string>();
    for (const group of groups) {
      const normalizedOid: string | undefined = this.normalizeOid(group.decidedByOid);
      if (group.decidedByOid && normalizedOid && !this.replacedByDisplayNames.has(normalizedOid)) {
        oidsToResolve.add(group.decidedByOid);
        this.resolvingDecidedByOids.add(normalizedOid);
      }
    }
    if (oidsToResolve.size === 0) { return; }

    // Follow the established app pattern (`piping-tabs-parent`): Graph's individual
    // `/users/{oid}` endpoint resolves reliably in environments where the batch `id in (...)`
    // filter can return an empty collection due to directory-query permissions.
    forkJoin(Array.from(oidsToResolve).map(oid => this.graphService.getUserByOid(oid).pipe(
      map((user: MicrosoftGraph.User | null) => ({ oid, user })),
      catchError(() => of({ oid, user: null })),
    ))).pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(results => {
      for (const { oid, user } of results) {
        const normalizedOid: string = this.normalizeOid(oid);
        const fallbackName: string = user
          ? [user.givenName, user.surname].filter((name): name is string => !!name).join(' ')
          : '';
        this.replacedByDisplayNames.set(normalizedOid, user?.displayName || fallbackName || 'User unavailable');
        this.resolvingDecidedByOids.delete(normalizedOid);
      }
    });
  }

  private normalizeOid(oid: string): string {
    return oid.trim().toLowerCase();
  }

  /** Returns a Graph display name without exposing the directory OID in the UI. */
  getDecidedByDisplayName(decidedByOid: string | undefined): string {
    if (!decidedByOid) { return ''; }
    const normalizedOid: string = this.normalizeOid(decidedByOid);
    return this.replacedByDisplayNames.get(normalizedOid)
      ?? (this.resolvingDecidedByOids.has(normalizedOid) ? 'Loading user…' : 'User unavailable');
  }

  /** Returns the comma-separated list of pipe class names a component group appears in (AC7). */
  getPipeClassNamesForGroup(group: InactiveComponentGroupDto): string {
    const pipeClassNames: string = (group.pipeClasses ?? [])
      .map((ref: InactiveComponentPipeClassRefDto) => ref.pipeClassName)
      .filter((name): name is string => !!name)
      .join(', ');
    return pipeClassNames || (this.isInactiveComponentRemoved(group) ? 'Removed from release revision' : '—');
  }

  /** Removed groups are retained by the API as immutable release-audit history. */
  isInactiveComponentRemoved(group: InactiveComponentGroupDto): boolean {
    return group.resolutionAction === InactiveComponentResolutionAction._4;
  }

  /**
   * Returns true when the resolution action buttons (Replace / Activate / Release As-Is) should
   * be shown for an inactive component group. The buttons must only be offered while at least one
   * of the group's pipe classes is set to Revise; when every occurrence is Retire or Release
   * As-Is, the SME cannot act on the component, so no action check is performed.
   */
  shouldShowInactiveComponentActions(group: InactiveComponentGroupDto): boolean {
    if (!this.pipingClassStatusData) { return false; }
    const allStatusRows: PipingClassStatusRowDto[] = [
      ...(this.pipingClassStatusData.nonReviewClasses ?? []),
      ...(this.pipingClassStatusData.modifyClasses ?? []),
      ...(this.pipingClassStatusData.releaseAsIsClasses ?? []),
      ...(this.pipingClassStatusData.retiredClasses ?? []),
    ];
    const pipeClassCloneIds: Set<string> = new Set<string>(
      (group.pipeClasses ?? []).map((ref: InactiveComponentPipeClassRefDto) => ref.pipeClassCloneId ?? ''),
    );
    return allStatusRows.some(
      row => pipeClassCloneIds.has(row.pipeClassCloneId ?? '') && row.action === ReleaseSelectedObject._1,
    );
  }

  // ── Attributes auto-save ──────────────────────────────────────────────

  /** Trims leading/trailing whitespace from the Name field when the input loses focus. */
  trimNameField(): void {
    const currentValue: string = this.attributesForm.get('name')?.value ?? '';
    this.attributesForm.get('name')?.setValue(currentValue.trim());
  }

  private autoSaveAttributes(): void {
    if (!this.attributesData) { return; }
    const updated: ReleaseAttributesDto = new ReleaseAttributesDto({
      ...this.attributesData,
      name: this.attributesForm.value.name,
      revision: this.attributesForm.value.revision,
      description: this.attributesForm.value.description,
    });
    this.apiClient.saveReleaseAttributes(this.releaseId, updated).pipe(
      indicate(this.savingAttributes$),
      catchError(err => {
        const fallbackMessage: string = err?.status === 409
          ? 'A release with the same name already exists for the selected Business Unit and Component Type.'
          : 'Failed to save attributes.';
        this.snackBar.showError(getApiErrorMessage(err, fallbackMessage));
        return EMPTY;
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(saved => {
      this.attributesData = saved;
    });
  }

  // ── Publish (Release button) ──────────────────────────────────────────

  /** True once the Publish Release button may be shown: user can manage the release and it's out of Draft/Published. */
  get canPublishRelease(): boolean {
    return this.canManageRelease
      && this.releaseStatus !== ReleaseStatus.DRAFT
      && this.releaseStatus !== ReleaseStatus.PUBLISHED;
  }

  /** Confirms with the user before publishing the release. */
  publishRelease(): void {
    if (!this.summary?.canPublish || !this.canPublishRelease) { return; }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '450px',
      data: {
        dialogMessage: 'This will publish this release. Do you wish to continue?',
        confirmText: 'yes',
        cancelText: 'cancel',
      },
    });

    dialogRef.afterClosed().pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.executePublishRelease();
      }
    });
  }

  /** Sends the publish request. On 204 navigates away; on 409 refreshes badges. */
  private executePublishRelease(): void {
    const attributes: ReleaseAttributesDto = this.attributesData ?? new ReleaseAttributesDto();
    const dto: ExecuteReleaseRequestDto = new ExecuteReleaseRequestDto({
      attributes,
      acknowledgedValidationCodes: [],
      actorOid: this.getCurrentUserOid(),
    });

    this.apiClient.publishRelease(this.releaseId, dto).pipe(
      indicate(this.releasing$),
      catchError(err => {
        if (err?.status === 409 && err?.error) {
          // 409 body is a ReleaseFinalChecksSummaryDto — update badges without navigating away.
          try {
            const blockedSummary: ReleaseFinalChecksSummaryDto = ReleaseFinalChecksSummaryDto.fromJS(
              typeof err.error === 'string' ? JSON.parse(err.error) : err.error,
            );
            this.summary = blockedSummary;
          } catch (_parseError) {
            // If parsing fails, fall back to reloading from the API.
            this.loadSummary();
          }
          this.snackBar.showError('Release blocked: resolve all errors and acknowledge all warnings before publishing.');
        } else if (err?.status === 400) {
          const fallbackMessage: string = 'Invalid attributes. Please correct the release attributes before publishing.';
          this.snackBar.showError(getApiErrorMessage(err, fallbackMessage));
        } else if (err?.status === 403) {
          this.snackBar.showError('You do not have permission to publish this release.');
        } else if (err?.status === 404) {
          this.snackBar.showError('Release not found.');
        } else {
          this.snackBar.showError(getApiErrorMessage(err, 'Failed to publish release.'));
        }
        return EMPTY;
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      this.snackBar.showInfo('Release published successfully.');
      this.router.navigate(['/piping/revisionsAndReleases', 'releases']);
    });
  }

  // ── Navigation ────────────────────────────────────────

  navigateBack(): void {
    this.router.navigate(['/piping/releases', this.releaseId]);
  }

  /**
   * Opens the pipe class detail page in a new browser tab, mirroring the behaviour
   * of `release-details`. Prefers `pipeClassCloneId` (the working copy inside the
   * release) and falls back to `pipeClassId` (the original) when no clone exists yet.
   */
  openPipeClassInNewTab(row: PipingClassStatusRowDto): void {
    const id: string = row.pipeClassCloneId ?? row.pipeClassId ?? '';
    if (!id) { return; }
    window.open(`/pipingsummary/${encodeURIComponent(id)}?isEditMode=true`, '_blank');
  }

  // ── Helpers used by the template ─────────────────────────────────────

  /** Returns the CSS severity class for a ValidaionSeverity value (template accessor). */
  getSeverityBadgeClass(severity: ValidationSeverity | undefined): string {
    return getSeverityCssClass(severity);
  }

  /** Returns the Final Checks-specific lifecycle label, prioritizing actionable successor states. */
  getValveStatusChipLabel(row: ValveStatusRowDto): string {
    if (row.hasNewerVersion && !row.newerVersionIsUnpublished) {
      return 'Newer Version Available';
    }
    if (row.pipeClassAction === ReleaseSelectedObject._2 || row.actingReleaseAction === ReleaseSelectedObject._2) {
      return 'Retiring';
    }
    if (row.hasNewerVersion && row.newerVersionIsUnpublished) {
      return 'Draft Update Pending';
    }
    return row.valveStatus === undefined ? 'Unknown' : RevisionStatusHelper.getDisplayName(row.valveStatus);
  }

  /** Returns the CSS class for the Final Checks-specific lifecycle label. */
  getValveStatusChipClass(row: ValveStatusRowDto): string {
    if (row.hasNewerVersion && !row.newerVersionIsUnpublished) {
      return 'newer-published-status';
    }
    if (row.pipeClassAction === ReleaseSelectedObject._2 || row.actingReleaseAction === ReleaseSelectedObject._2) {
      return 'retiring-status';
    }
    if (row.hasNewerVersion && row.newerVersionIsUnpublished) {
      return 'newer-draft-status';
    }
    return row.valveStatus === undefined ? 'unknown-status' : RevisionStatusHelper.getCssClass(row.valveStatus);
  }

  /** Returns true if the inactive component group is still Pending (no resolution action taken yet). */
  isInactiveComponentPending(group: InactiveComponentGroupDto): boolean {
    return (group.resolutionAction ?? InactiveComponentResolutionAction._0) === InactiveComponentResolutionAction._0;
  }

  /**
   * Returns the display tag for a valve, keeping only the portion before the first
   * underscore (e.g. `V-1234_A` -> `V-1234`), matching the convention used elsewhere
   * in the app (see `valve-summary-page` and `valve-tab`).
   */
  getValveTagPrefix(valveTag: string | undefined): string {
    return valveTag?.split('_')[0] ?? '';
  }

  /**
   * Returns the row's message with the full valve tag replaced by its display prefix
   * (e.g. `V-1234_A` -> `V-1234`), so the Message column matches the Valve Tag column.
   * The backend message text embeds the full tag verbatim, so a plain string replace is safe.
   */
  getValveStatusMessage(row: ValveStatusRowDto): string {
    const message: string = row.message ?? '';
    const fullTag: string = row.valveTag ?? '';
    if (!fullTag || !message.includes(fullTag)) {
      return message;
    }
    return message.split(fullTag).join(this.getValveTagPrefix(fullTag));
  }

  /** Returns "OK" (nothing to resolve) instead of "Info" for Valve Status severity, styled in green. */
  getValveStatusSeverityLabel(severity: ValidationSeverity | undefined): string {
    return severity === ValidationSeverity._0 ? 'OK' : getSeverityLabel(severity);
  }

  /** Returns the CSS class for Valve Status severity, styling Info as green ("OK") instead of blue. */
  getValveStatusSeverityClass(severity: ValidationSeverity | undefined): string {
    return severity === ValidationSeverity._0 ? 'severity-ok' : getSeverityCssClass(severity);
  }


  private sortValveStatusRows(rows: ValveStatusRowDto[] | undefined): ValveStatusRowDto[] {
    return this.groupValveStatusRows(rows).sort((a, b) => {
      const displayStatusDiff: number = this.getValveStatusSortPriority(a) - this.getValveStatusSortPriority(b);
      if (displayStatusDiff !== 0) { return displayStatusDiff; }
      const severityDiff: number = (b.severity ?? ValidationSeverity._0) - (a.severity ?? ValidationSeverity._0);
      if (severityDiff !== 0) { return severityDiff; }
      return this.getValveTagPrefix(a.valveTag).localeCompare(this.getValveTagPrefix(b.valveTag));
    });
  }

  /** Groups a status section into one display row per valve, listing each referencing pipe class. */
  private groupValveStatusRows(rows: ValveStatusRowDto[] | undefined): ValveStatusRowDto[] {
    const rowsByValveId: Map<string, ValveStatusRowDto> = new Map<string, ValveStatusRowDto>();
    const pipeClassNamesByValveId: Map<string, Set<string>> = new Map<string, Set<string>>();
    const rowsWithoutValveId: ValveStatusRowDto[] = [];

    for (const row of rows ?? []) {
      if (!row.valveId) {
        rowsWithoutValveId.push(new ValveStatusRowDto(row));
        continue;
      }

      if (!rowsByValveId.has(row.valveId)) {
        rowsByValveId.set(row.valveId, new ValveStatusRowDto(row));
        pipeClassNamesByValveId.set(row.valveId, new Set<string>());
      }

      if (row.pipeClassName) {
        pipeClassNamesByValveId.get(row.valveId)?.add(row.pipeClassName);
      }
    }

    const groupedRows: ValveStatusRowDto[] = Array.from(rowsByValveId.entries()).map(
      ([valveId, row]: [string, ValveStatusRowDto]) => {
        row.pipeClassName = Array.from(pipeClassNamesByValveId.get(valveId) ?? new Set<string>()).join(', ');
        return row;
      },
    );

    return [...groupedRows, ...rowsWithoutValveId];
  }

  /** Returns the Valve Status tab display order, keeping outside-release valves above ordinary published valves. */
  private getValveStatusSortPriority(row: ValveStatusRowDto): number {
    if (row.hasNewerVersion && !row.newerVersionIsUnpublished) { return 0; }
    if (row.pipeClassAction === ReleaseSelectedObject._2 || row.actingReleaseAction === ReleaseSelectedObject._2) { return 1; }
    if (row.hasNewerVersion && row.newerVersionIsUnpublished) { return 2; }
    if (row.isOutsideSelectedValveRelease === true) { return 3; }
    return 4;
  }

  // ── Valve Status: update to newer version ─────────────────────────────

  /**
   * Returns true when the row can be bulk-updated to a newer valve version.
   * The backend only sets this when a PUBLISHED successor exists and the referencing pipe class is
   * being revised, so a valve whose only newer revision is still a draft is never offered.
   */
  canUpdateValveToLatest(row: ValveStatusRowDto): boolean {
    return this.enableValveVersionUpdates && row.canUpdateToLatestVersion === true;
  }

  /** Returns true when the current user can enable the valve represented by the row. */
  canEnableValve(row: ValveStatusRowDto): boolean {
    return row.isDisabled === true && this.canEnableValves;
  }

  /** Returns true if the given valve is currently selected for the "update to newer version" action. */
  isValveSelectedForUpdate(valveId: string | undefined): boolean {
    return valveId != null && this.selectedValveIdsForUpdate.has(valveId);
  }

  /** Adds or removes a valve from the "update to newer version" selection. */
  toggleValveSelectionForUpdate(valveId: string | undefined, checked: boolean): void {
    if (!valveId) { return; }
    if (checked) {
      this.selectedValveIdsForUpdate.add(valveId);
    } else {
      this.selectedValveIdsForUpdate.delete(valveId);
    }
  }

  /** Selects or clears every updatable valve across all three sections in one click. */
  toggleAllValvesForUpdate(checked: boolean): void {
    if (!checked) {
      this.selectedValveIdsForUpdate.clear();
      return;
    }
    for (const row of this.getAllValveStatusRows()) {
      if (this.canUpdateValveToLatest(row) && row.valveId) {
        this.selectedValveIdsForUpdate.add(row.valveId);
      }
    }
  }

  /** Total number of distinct valves that can be updated to a newer published version. */
  getUpdatableValveCount(): number {
    const updatableIds: Set<string> = new Set<string>();
    for (const row of this.getAllValveStatusRows()) {
      if (this.canUpdateValveToLatest(row) && row.valveId) {
        updatableIds.add(row.valveId);
      }
    }
    return updatableIds.size;
  }

  /** Opens a confirmation modal before updating all selected valves to their newer published version. */
  openUpdateSelectedValvesDialog(): void {
    if (!this.enableValveVersionUpdates || this.selectedValveIdsForUpdate.size === 0) { return; }
    const count: number = this.selectedValveIdsForUpdate.size;
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '480px',
      data: {
        dialogMessage: `Update ${count} valve(s) to their latest published version? .`,
        confirmText: 'Update',
        cancelText: 'Cancel',
      },
    });

    dialogRef.afterClosed().pipe(
      first(),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(confirmed => {
      if (!confirmed) { return; }
      this.updateSelectedValvesToLatestVersion();
    });
  }

  /**
   * Sends the bulk update request and replaces the cached tab data with the refreshed payload the
   * backend returns, so no extra round-trip is needed. Valves the backend could not update are
   * reported individually rather than failing the whole batch.
   */
  private updateSelectedValvesToLatestVersion(): void {
    if (!this.enableValveVersionUpdates) { return; }
    const dto: ValveVersionUpdateRequestDto = new ValveVersionUpdateRequestDto({
      valveIds: Array.from(this.selectedValveIdsForUpdate),
      actorOid: this.getCurrentUserOid(),
    });

    this.apiClient.updateValvesToLatestVersion(this.releaseId, dto).pipe(
      indicate(this.updatingValveVersions$),
      catchError(err => {
        this.snackBar.showError(getApiErrorMessage(err, 'Failed to update the selected valves.'));
        return EMPTY;
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((result: ValveVersionUpdateResultDto) => {
      this.selectedValveIdsForUpdate.clear();
      this.applyRefreshedValveStatus(result.valveStatus);

      if (result.updatedValveCount) {
        this.snackBar.showInfo(
          `${result.updatedValveCount} valve(s) updated across ${result.updatedPipeClassCount} pipe class(es).`,
        );
      }
      this.reportSkippedValveUpdates(result.skipped ?? []);
      this.loadSummary();
    });
  }

  /** Surfaces the backend's per-valve skip reasons so a partial update is never silent. */
  private reportSkippedValveUpdates(skipped: ValveVersionUpdateSkipDto[]): void {
    if (skipped.length === 0) { return; }
    const firstReason: string = skipped[0].reason ?? '';
    const suffix: string = skipped.length > 1 ? ` (and ${skipped.length - 1} more)` : '';
    this.snackBar.showWarning(`${skipped.length} valve(s) could not be updated: ${firstReason}${suffix}`);
  }

  // ── Valve Status: enable a disabled valve ─────────────────────────────

  /**
   * Opens a confirmation modal before re-enabling a disabled valve. Only offered to users holding
   * the same dictionary-write permission required to disable it in the first place; the backend
   * enforces the same rule, so this check is presentational only.
   */
  openEnableValveDialog(row: ValveStatusRowDto): void {
    if (!this.canEnableValves || row.isDisabled !== true) { return; }
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '480px',
      data: {
        dialogMessage: `Enable valve ${this.getValveTagPrefix(row.valveTag)}? `
          + 'This re-activates it in the component catalog for every pipe class, not just this release.',
        confirmText: 'Enable',
        cancelText: 'Cancel',
      },
    });

    dialogRef.afterClosed().pipe(
      first(),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(confirmed => {
      if (!confirmed) { return; }
      this.enableValve(row);
    });
  }

  private enableValve(row: ValveStatusRowDto): void {
    const dto: ValveActivationRequestDto = new ValveActivationRequestDto({
      valveId: row.valveId,
      actorOid: this.getCurrentUserOid(),
    });

    this.apiClient.enableValve(this.releaseId, dto).pipe(
      indicate(this.enablingValve$),
      catchError(err => {
        this.snackBar.showError(getApiErrorMessage(err, 'Failed to enable the valve.'));
        return EMPTY;
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((refreshed: ValveStatusValidationDto) => {
      this.applyRefreshedValveStatus(refreshed);
      this.snackBar.showInfo(`Valve ${this.getValveTagPrefix(row.valveTag)} enabled.`);
      this.loadSummary();
    });
  }

  // ── Valve Status: release-wide "proceed without updates" toggle ───────

  /** True when at least one valve needs the user to knowingly proceed without updating it. */
  requiresProceedWithoutUpdates(): boolean {
    return this.valveStatusData?.requiresProceedWithoutUpdates === true;
  }

  /** Current persisted state of the release-wide acknowledgement. */
  isProceedWithoutUpdatesEnabled(): boolean {
    return this.valveStatusData?.proceedWithoutUpdates === true;
  }

  /**
   * Handles the "proceed without updates" toggle. Switching it ON requires an explicit confirmation
   * because it waives every draft / disabled / superseded valve warning at once and unblocks
   * publishing; switching it OFF is reversible and applies immediately.
   * @param checked The state the user just switched the toggle to.
   * @param toggle The toggle instance, reset directly when the user cancels. The `[checked]` input is
   * one-way, so Angular will not revert the visual state on its own when the bound value is unchanged.
   */
  onProceedWithoutUpdatesChange(checked: boolean, toggle: MatSlideToggle): void {
    if (!checked) {
      this.saveProceedWithoutUpdates(false);
      return;
    }

    const affectedCount: number = this.getRowsRequiringConfirmation().length;
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '520px',
      data: {
        dialogMessage: `${affectedCount} valve entr(ies) are still in draft, disabled, or superseded by a newer version. `
          + 'Proceeding will publish this release without updating them. Continue?',
        confirmText: 'Proceed without updates',
        cancelText: 'Cancel',
      },
    });

    dialogRef.afterClosed().pipe(
      first(),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(confirmed => {
      if (!confirmed) {
        toggle.checked = false;
        return;
      }
      this.saveProceedWithoutUpdates(true);
    });
  }

  private saveProceedWithoutUpdates(acknowledged: boolean): void {
    const dto: ValveProceedWithoutUpdatesRequestDto = new ValveProceedWithoutUpdatesRequestDto({
      acknowledged,
      actorOid: this.getCurrentUserOid(),
      notes: undefined,
    });

    this.apiClient.setProceedWithoutUpdates(this.releaseId, dto).pipe(
      indicate(this.savingProceedWithoutUpdates$),
      catchError(err => {
        this.snackBar.showError(getApiErrorMessage(err, 'Failed to save the acknowledgement.'));
        // Reload so the toggle reflects what is actually persisted.
        this.valveStatusData = null;
        this.loadValveStatus();
        return EMPTY;
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((refreshed: ValveStatusValidationDto) => {
      this.applyRefreshedValveStatus(refreshed);
      this.loadSummary();
    });
  }

  /** Returns every row across all sections that still needs the user's confirmation. */
  private getRowsRequiringConfirmation(): ValveStatusRowDto[] {
    return this.getAllValveStatusRows().filter(row => row.requiresUserConfirmation === true);
  }

  /** Flattens the three valve status sections into one list. */
  private getAllValveStatusRows(): ValveStatusRowDto[] {
    if (!this.valveStatusData) { return []; }
    return [
      ...(this.valveStatusData.modifyClassValves ?? []),
      ...(this.valveStatusData.releaseAsIsClassValves ?? []),
      ...(this.valveStatusData.retireClassValves ?? []),
    ];
  }

  /** Replaces the cached tab data with a freshly-computed payload returned by an action endpoint. */
  private applyRefreshedValveStatus(data: ValveStatusValidationDto | undefined): void {
    if (!data) { return; }
    data.modifyClassValves = this.sortValveStatusRows(data.modifyClassValves);
    data.releaseAsIsClassValves = this.sortValveStatusRows(data.releaseAsIsClassValves);
    data.retireClassValves = this.sortValveStatusRows(data.retireClassValves);
    this.valveStatusData = data;
  }

  // ── Valve Descriptions: pipe classes column ───────────────────────────

  /**
   * Returns the pipe class name for one Valve Descriptions row. A description row represents a
   * single valve-in-pipe-class relationship, so matching on both ids prevents grouping every
   * pipe class that happens to use the same valve into one cell.
   */
  getPipeClassForValveDescription(row: ValveDescriptionRowDto): string {
    if (!row.valveId || !row.pipeClassId || !this.valveStatusData) {
      return row.pipeClassId ?? '';
    }
    const allRows: ValveStatusRowDto[] = [
      ...(this.valveStatusData.modifyClassValves ?? []),
      ...(this.valveStatusData.releaseAsIsClassValves ?? []),
      ...(this.valveStatusData.retireClassValves ?? []),
    ];
    const matchingRow: ValveStatusRowDto | undefined = allRows.find(statusRow =>
      statusRow.valveId === row.valveId && statusRow.pipeClassId === row.pipeClassId
    );
    return matchingRow?.pipeClassName ?? row.pipeClassId;
  }

  /**
   * Returns the badge state for the Valve Descriptions tab.
   * Shows 'warning' if any row has a description mismatch, 'ok' otherwise.
   */
  getValveDescriptionTabBadgeState(): 'ok' | 'warning' {
    // Valve Descriptions data is loaded lazily when its tab is opened. Until then, use the
    // eagerly loaded summary so its warning badge is visible as soon as Final System Checks
    // opens, rather than only after the user visits the tab.
    if (!this.valveDescriptionsData || this.staleTabIndexes.has(ReleaseFinalChecksComponent.VALVE_DESCRIPTIONS_TAB_INDEX)) {
      const summaryState: 'error' | 'warning' | 'ok' = this.getBadgeState(this.summary?.valveDescriptions);
      return summaryState === 'ok' ? 'ok' : 'warning';
    }
    const allRows: ValveDescriptionRowDto[] = [
      ...(this.valveDescriptionsData.modifyClassValves ?? []),
      ...(this.valveDescriptionsData.releaseAsIsClassValves ?? []),
      ...(this.valveDescriptionsData.retireClassValves ?? []),
    ];
    const hasWarning: boolean = allRows.some(row => this.hasDescriptionWarning(row));
    return hasWarning ? 'warning' : 'ok';
  }

  /**
   * Checks if a valve description row has a warning (old and new values don't match).
   * Once the user has saved a choice for this row, the mismatch is considered resolved even if
   * the backend still reports `descriptionMismatch === true` for the "Use Generated" option --
   * the mismatch was acknowledged and the draft value was already updated server-side.
   */
  hasDescriptionWarning(row: ValveDescriptionRowDto): boolean {
    if (row.chosenOption != null) { return false; }
    return row.descriptionMismatch === true && (row.oldValue ?? '') !== (row.newValue ?? '');
  }

  /** Returns true when at least one currently loaded valve-description row is still unresolved. */
  hasUnresolvedValveDescriptionConflicts(): boolean {
    const allRows: ValveDescriptionRowDto[] = this.getValveDescriptionRows();
    return allRows.some((row: ValveDescriptionRowDto) => this.hasDescriptionWarning(row));
  }

  /** Returns whether the two displayed descriptions differ, regardless of the saved choice. */
  hasDescriptionDifference(row: ValveDescriptionRowDto): boolean {
    return (row.oldValue ?? '') !== (row.newValue ?? '');
  }

  /**
   * Returns the effective display value for the new description column.
   * If new value is empty, returns old value instead (and no radio button is shown).
   */
  getValveDescriptionDisplayValue(row: ValveDescriptionRowDto): string {
    return (row.newValue ?? '').trim().length > 0 ? (row.newValue ?? '') : (row.oldValue ?? '');
  }

  /**
   * Returns a unique key for a description row's radio-button `name` attribute.
   * `valveId` alone is not unique -- the same valve catalog entry (and thus the same `valveId`)
   * can appear in multiple pipe classes within a release (e.g. GASMSM1C in both 1SA04 and
   * 3SA03). Radio inputs sharing the same `name` form a single mutually-exclusive group in the
   * DOM, so using only `valveId` caused selecting a description in one pipe class's row to also
   * toggle/clear the radio in every other row for that same valve ("mirror" bug). Combining
   * `valveId` with `pipeClassId` scopes each row's radio group to that specific row.
   */
  getDescriptionRowKey(row: ValveDescriptionRowDto): string {
    return `desc-${row.valveId ?? ''}-${row.pipeClassId ?? ''}`;
  }

  /**
   * Returns true if the row should show radio buttons (only when there's a mismatch
   * or a previously saved choice, AND the new value is not empty, AND the row's pipe class
   * is being Revised). Release As-Is and Retire pipe classes are not being modified in this
   * release, so their valve descriptions cannot be changed here.
   */
  shouldShowDescriptionChoice(row: ValveDescriptionRowDto): boolean {
    if (row.pipeClassAction !== ReleaseSelectedObject._1) { return false; }
    const hasNewValue: boolean = (row.newValue ?? '').trim().length > 0;
    const hasChosenOption: boolean = row.chosenOption != null;
    return hasNewValue && (row.descriptionMismatch === true || hasChosenOption);
  }

  /**
   * Sorts valve description rows by:
   * 1. Rows with unresolved warnings first
   * 2. Resolved rows that remain editable (show the pencil) next
   * 3. Read-only rows last
   * 4. Then by valve tag ascending
   * 5. Then by pipe class name ascending
   */
  getSortedValveDescriptionRows(rows: ValveDescriptionRowDto[] | undefined): ValveDescriptionRowDto[] {
    if (!rows) { return []; }
    return [...rows].sort((a, b) => {
      // Pending warning rows come first. Resolved Revise rows retain their controls/pencil and
      // come next; Release As-Is and Retire rows are read-only and remain at the bottom.
      const getPriority = (row: ValveDescriptionRowDto): number => {
        if (this.hasDescriptionWarning(row)) { return 0; }
        if (this.shouldShowDescriptionChoice(row)) { return 1; }
        return 2;
      };
      const priorityDiff: number = getPriority(a) - getPriority(b);
      if (priorityDiff !== 0) {
        return priorityDiff;
      }
      // Within a priority group, sort by valve tag.
      const aTag: string = (a.valveTag ?? '').toLowerCase();
      const bTag: string = (b.valveTag ?? '').toLowerCase();
      if (aTag !== bTag) {
        return aTag.localeCompare(bTag);
      }
      // Finally, sort equal valve tags by their individual pipe class.
      const aClass: string = this.getPipeClassForValveDescription(a).toLowerCase();
      const bClass: string = this.getPipeClassForValveDescription(b).toLowerCase();
      return aClass.localeCompare(bClass);
    });
  }

  // ── Export functions ──────────────────────────────────────────────────

  /**
   * Downloads the Valve Descriptions Excel file directly from the Final System Checks endpoint.
   * The generated API client requests a Blob and extracts the backend-provided Content-Disposition
   * file name. The fallback is used only if that header does not contain a file name.
   */
  onExportValveDescriptions(): void {
    this.downloadService.downloadFile(
      this.apiClient.exportValveDescriptions(this.releaseId).pipe(
        map(response => ({
          ...response,
          fileName: response.fileName || 'Valve Descriptions.xlsx',
        })),
      ),
    );
  }

}
